import { useEffect, useState } from 'react';
import { Container, Typography, makeStyles, Snackbar } from '@material-ui/core';
import { Edit } from '@material-ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import { string, object } from 'yup';
import { format } from 'date-fns';

import SearchInput from '../tableElements/searchInput';
import Table from '../tableElements/table';
import ManagementModal from '../managementModal';
import Notification from 'src/components/ui/notification';

import { Column } from '../tableElements/types';
import { Site } from './types';

import { getSitesData } from 'src/state/entities/sites/selectors';
import { useCurrent } from 'src/helpers/hooks';
import { getSites, editSite } from 'src/state/entities/sites/actions';
import { getUtilities } from 'src/state/entities/utilities/selectors';
import { getUtilitiesList } from 'src/state/entities/utilities/actions';
import { getCustomersList } from 'src/state/entities/customers/actions';
import { getCustomers } from 'src/state/entities/customers/selectors';

const useStyles = makeStyles((theme) => ({
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '20px',
    padding: '20px 0 0',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  container: {
    padding: '20px',
    width: '100%',
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
  },
}));

const columns: Column[] = [
  { id: 'id', label: 'Id' },
  { id: 'name', label: 'Name' },
  {
    id: 'installationDate',
    label: 'Installation Date',
    formatData: (value: string | number | string[]) => {
      if (value && typeof value === 'string') {
        return format(new Date(value), 'MM-dd-yyyy');
      }

      return '';
    },
  },
  {
    id: 'address',
    label: 'Address',
  },
  {
    id: 'utilityName',
    label: 'Utility',
  },
  {
    id: 'customerName',
    label: 'Customer',
  },
  {
    id: 'actions',
    label: '',
    action: true,
  },
];

const validationSchema = object({
  name: string().min(0, 'Name is required.').required('Name is required.'),
});

const Sites = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const sitesData = useSelector(getSitesData);
  const utilitiesList = useSelector(getUtilities);
  const customersList = useSelector(getCustomers);
  const [searchValue, setSearchValue] = useState('');
  const [editData, setEditData] = useState<Site | null>(null);
  const [showMessage, setShowMessage] = useState(false);

  const action = useCurrent(getSites.request(1, searchValue));
  const actionUtilities = useCurrent(getUtilitiesList.request());
  const actionCustomers = useCurrent(getCustomersList.request());

  useEffect(() => {
    if (action.current && !sitesData.items.length) {
      dispatch(action.current);
    }
  }, [action, dispatch]);

  useEffect(() => {
    if (actionUtilities.current && !utilitiesList.length) {
      dispatch(actionUtilities.current);
    }
  }, [actionUtilities, dispatch]);

  useEffect(() => {
    if (actionCustomers.current && !customersList.length) {
      dispatch(actionCustomers.current);
    }
  }, [actionCustomers, dispatch]);

  const handleChangePage = (event: React.ChangeEvent<unknown>, value: number) => {
    dispatch(getSites.request(value, searchValue));
  };

  const handleSearch = (searchText: string) => {
    setSearchValue(searchText);
    dispatch(getSites.request(1, searchText));
  };

  const handleEdit = (id: Number) => {
    setEditData(sitesData.items.find((site) => site.id === id) || null);
  };

  const onSubmit = (values: Record<string, string | number>) => {
    let { id, name, address, utilityId, customerId } = values as unknown as Site;

    utilityId = Number(utilityId);
    customerId = Number(customerId);

    const modifiedSiteValues = {
      name,
      address,
      utilityId,
      customerId,
      utilityName: utilitiesList.find((utility) => utility.id === utilityId)?.name,
    };

    dispatch(editSite.request(id as number, modifiedSiteValues as Site));
  };

  const handleClose = () => {
    setEditData(null);
  };

  useEffect(() => {
    if (!sitesData.saving && !sitesData.error) {
      setEditData(null);

      dispatch(getSites.request(sitesData.pageNumber, searchValue));
    }
  }, [sitesData.saving]);

  useEffect(() => {
    if (sitesData.error || sitesData.success) {
      setShowMessage(true);
    }
  }, [sitesData.error, sitesData.success]);

  const actions = [
    {
      Icon: Edit,
      action: handleEdit,
      title: 'Edit',
    },
  ];

  return (
    <Container className={classes.container}>
      <Container className={classes.header}>
        <Typography className={classes.title}>Sites Management</Typography>
        <SearchInput handleSearch={handleSearch} placeholder="Search site..." />
      </Container>
      <Table
        actions={actions}
        columns={columns}
        data={sitesData.loading ? [] : sitesData.items}
        emptyStateMessage="No sites found"
        handleChangePage={handleChangePage}
        isLoading={sitesData.loading}
        pageNumber={sitesData.pageNumber}
        perPage={10}
        total={sitesData.totalItems}
      />
      <ManagementModal
        fields={[
          { id: 'name', label: 'Name' },
          { id: 'installationDate', label: 'Installation Date', disabled: true },
          { id: 'address', label: 'Address' },
          { id: 'utilityId', label: 'Utility', list: utilitiesList },
          { id: 'customerId', label: 'Customer', list: customersList },
        ]}
        initialValues={
          (editData || {
            name: '',
            installationDate: '',
            address: '',
            utilityId: '',
            utilityName: '',
            customerId: '',
            customerName: '',
          }) as Record<string, string | number>
        }
        isOpen={!!editData}
        isSubmitting={!!sitesData.saving}
        onClose={handleClose}
        onSubmit={onSubmit}
        title={'Edit site'}
        validationSchema={validationSchema}
      />
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={3000}
        onClose={() => setShowMessage(false)}
        open={showMessage}
      >
        <Notification severity={sitesData.error ? 'error' : 'success'}>
          {sitesData.error
            ? typeof sitesData.error === 'string'
              ? sitesData.error
              : 'Something went wrong. Please try again.'
            : 'The site was saved.'}
        </Notification>
      </Snackbar>
    </Container>
  );
};

export default Sites;
